import Vue from 'vue';
import { ActionContext, ActionTree, Module, MutationTree } from 'vuex';
import { t } from '@/utils/translation';
import {
  DriverTabStore,
  DriverManagementState,
  Driver,
  NewDriver,
  DriverGetParams,
  DriverListMeta,
  CompanyDriverStatus,
  DriverRequestAction,
  DriverTab,
  Drivers,
} from './driver-management.model';
import { driverManagementService } from './driver-management.service';
import { GETTER_CURRENT_COMPANY_ID } from '../company/company.store';
import { isAccessible } from '@/utils/permissions';

const filterOutDriver = (driverId: string, tab: DriverTab, store: ActionContext<DriverManagementState, any>) => {
  const tabState = store.state[tab];
  if (tabState) {
    tabState.drivers.data = tabState.drivers.data.filter((driver) => driver.id !== driverId);
    tabState.drivers.meta.total -= tabState.drivers.data.filter((driver) => driver.id === driverId).length;
    store.commit('SET_TAB_DRIVERS', { drivers: tabState.drivers, page: 1, tab });
  }
};

class DriverManagementStore implements Module<DriverManagementState, any> {
  public namespaced: boolean = true;
  public state: DriverManagementState = {
    drivers: [],
    pendingTotal: -1,
    activeTotal: -1,
    meta: {
      total: 0,
    },
    invitationUrl: '',
    activeDriverIdMenuAction: '',
    [DriverTab.PENDING]: {
      drivers: {
        data: [],
        meta: {
          total: 0,
        },
      },
      page: 1,
    },
  };

  public actions = {
    async getAll(store: ActionContext<DriverManagementState, any>, getParams: DriverGetParams) {
      try {
        const hasDriverManagerPermission = isAccessible('Drivers', store.rootState.users.roles);
        if (!hasDriverManagerPermission) {
          return;
        }

        const drivers = await driverManagementService.getAll({
          ...getParams,
          refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID],
        });
        store.commit('SET_DRIVERS', drivers.data);
        store.commit('SET_DRIVERS_META', drivers.meta);
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    async getTabDrivers(store: ActionContext<DriverManagementState, any>, params: DriverGetParams & { tab: DriverTab; page: number; useCache?: boolean }) {
      try {
        if (params.useCache) {
          return;
        }

        const drivers = await driverManagementService.getAll({
          ...params,
          refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID],
        });

        store.commit('SET_TAB_DRIVERS', { drivers, page: params.page, tab: params.tab });
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    async getPendingTotal(store: ActionContext<DriverManagementState, any>) {
      try {
        const { meta } = await driverManagementService.getAll({
          status: CompanyDriverStatus.DRIVER_REQUEST_PENDING,
          limit: 1,
          refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID],
        });

        store.commit('SET_PENDING_TOTAL', meta.total);
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    async getActiveTotal(store: ActionContext<DriverManagementState, any>) {
      try {
        const hasDriverManagerPermission = isAccessible('Drivers', store.rootState.users.roles);
        if (!hasDriverManagerPermission) {
          return;
        }

        const { meta } = await driverManagementService.getAll({
          status: CompanyDriverStatus.INVITATION_ACCEPTED,
          limit: 1,
          refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID],
        });

        store.commit('SET_ACTIVE_TOTAL', meta.total);
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    async removeDriver(store: ActionContext<DriverManagementState, any>, driverId: string) {
      try {
        await driverManagementService.remove({ id: store.rootGetters[GETTER_CURRENT_COMPANY_ID], driverId });
        filterOutDriver(driverId, DriverTab.INACTIVE, store);
        filterOutDriver(driverId, DriverTab.PENDING, store);
        Vue.utils.$toast(t('driver_removed_successfully'), 'success');
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    /**
     * Update driver's business email value using DriverManagementService.updateDriver method
     *
     * @param store
     * @param params
     */
    async updateDriversBusinessEmail(store: ActionContext<DriverManagementState, any>, params: { driver: Driver; newBusinessEmail: string }) {
      try {
        await driverManagementService.updateBusinessEmail({
          refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID],
          driverId: params.driver.id,
          businessEmail: params.newBusinessEmail,
        });
        Vue.utils.$toast(t('business_email_update_success'), 'success');
      } catch (err) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    async sendInvitations(store: ActionContext<DriverManagementState, any>, drivers: NewDriver[]) {
      const { data } = await driverManagementService.invite({ id: store.rootGetters[GETTER_CURRENT_COMPANY_ID], drivers });
      const tab = DriverTab.PENDING;
      const pending = store.state[tab];
      if (pending) {
        pending.drivers.data = data.concat(pending.drivers.data);
        pending.drivers.meta.total += data.length;
        store.commit('SET_TAB_DRIVERS', { drivers: pending.drivers, page: 1, tab });
      }

      Vue.utils.$toast(t('invitation_sent_successfully'), 'success');
    },

    setActiveDriverIdMenuAction(store: ActionContext<DriverManagementState, any>, driverId: string) {
      store.commit('SET_ACTIVE_DRIVER_ID_MENU_DRIVER', driverId);
    },

    async deactivateDriver(store: ActionContext<DriverManagementState, any>, driverId: string) {
      try {
        await driverManagementService.deactivate({ refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID], driverId });
        filterOutDriver(driverId, DriverTab.ACTIVE, store);

        Vue.utils.$toast(t('driver_deactivated_successfully'), 'success');
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },
    async reinviteDriver(store: ActionContext<DriverManagementState, any>, driverId: string) {
      try {
        await driverManagementService.reinviteDriver({ refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID], driverId });
        filterOutDriver(driverId, DriverTab.INACTIVE, store);
        Vue.utils.$toast(t('driver_reinvited_successfully'), 'success');
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },
    async acceptDrivers(store: ActionContext<DriverManagementState, any>, requestIds: string[]) {
      try {
        await driverManagementService.proceessPendingDrivers({ refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID], requestIds, action: DriverRequestAction.ACCEPT });
        const pendingTab = store.state[DriverTab.PENDING]!;
        store.dispatch('removeDriverFromTab', {
          driversTab: pendingTab,
          driverIds: requestIds,
        });

        Vue.utils.$toast(t('driver_accepted_successfully'), 'success');
      } catch (error) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
        throw error;
      }
    },
    async rejectDrivers(store: ActionContext<DriverManagementState, any>, requestIds: string[]) {
      try {
        await driverManagementService.proceessPendingDrivers({ refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID], requestIds, action: DriverRequestAction.REJECT });

        const pendingTab = store.state[DriverTab.PENDING]!;
        store.dispatch('removeDriverFromTab', {
          driversTab: pendingTab,
          driverIds: requestIds,
        });

        Vue.utils.$toast(t('driver_rejected_successfully'), 'success');
      } catch (error) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
        throw error;
      }
    },

    async getInvitationToken(store: ActionContext<DriverManagementState, any>) {
      try {
        if (store.state.invitationUrl) {
          return;
        }

        const response = await driverManagementService.getInvitationTokens(store.rootGetters[GETTER_CURRENT_COMPANY_ID]);
        if (!response.data.length) {
          return store.dispatch('generateInvitationToken');
        }

        store.commit('SET_INVITATION_URL', response.data[0].meta.invitationUrl);
      } catch (error) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
        throw error;
      }
    },

    async generateInvitationToken(store: ActionContext<DriverManagementState, any>) {
      try {
        const response = await driverManagementService.generateInvitationToken(store.rootGetters[GETTER_CURRENT_COMPANY_ID]);
        store.commit('SET_INVITATION_URL', response.meta.invitationUrl);
      } catch (error) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
        throw error;
      }
    },

    removeDriverFromTab(store: ActionContext<DriverManagementState, any>, { driversTab, driverIds }: { driversTab: DriverTabStore; driverIds: string[] }) {
      const driversToRemove = driversTab.drivers.data.filter((driver) => driverIds.includes(driver.id));
      driversToRemove.forEach((rejectedDriver) => {
        const driverIndex = driversTab.drivers.data.indexOf(rejectedDriver);
        driversTab.drivers.data.splice(driverIndex, 1);
      });
      driversTab.drivers.meta.total -= driversToRemove.length;

      store.commit('SET_PENDING_TOTAL', store.state.pendingTotal - driversToRemove.length);
    },
  } as ActionTree<DriverManagementState, any>;

  public mutations = {
    SET_DRIVERS(state: DriverManagementState, payload: Driver[]) {
      state.drivers = payload;
    },
    SET_TAB_DRIVERS(state: DriverManagementState, payload: { drivers: Drivers; tab: DriverTab; page: number }) {
      state[payload.tab] = payload;
    },
    SET_PENDING_TOTAL(state: DriverManagementState, pendingTotal: number) {
      state.pendingTotal = pendingTotal;
    },
    SET_ACTIVE_TOTAL(state: DriverManagementState, activeTotal: number) {
      state.activeTotal = activeTotal;
    },
    SET_DRIVERS_META(state: DriverManagementState, meta: DriverListMeta) {
      state.meta = meta;
    },
    SET_ACTIVE_DRIVER_ID_MENU_DRIVER(state: DriverManagementState, driverId: string) {
      state.activeDriverIdMenuAction = driverId;
    },
    SET_INVITATION_URL(state: DriverManagementState, payload: string) {
      state.invitationUrl = payload;
    },
  } as MutationTree<DriverManagementState>;
}

export default new DriverManagementStore();
